import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { OrderReferenceChip } from 'components/fields/OrderReferenceChip';
import React, { useState } from 'react';
import { Confirm, useTranslate } from 'react-admin';

import { RemainingPalletRow, RemainingPalletsPreview } from '../services/configApi';
import { DialogBase } from './DialogBase';

interface RemainingPalletsDialogProps {
  open: boolean;
  isLoading: boolean;
  isGenerating: boolean;
  data: RemainingPalletsPreview | null;
  onClose: () => void;
  onGenerate: () => void;
}

const PalletRowsTable: React.FC<{ rows: RemainingPalletRow[] }> = ({ rows }) => {
  const translate = useTranslate();
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>
            {translate('osfc.storage_ui.inventorization.remaining_pallets.order')}
          </TableCell>
          <TableCell>
            {translate('osfc.storage_ui.inventorization.remaining_pallets.customer')}
          </TableCell>
          <TableCell align="right">
            {translate('osfc.storage_ui.inventorization.remaining_pallets.customer_id')}
          </TableCell>
          <TableCell>
            {translate('osfc.storage_ui.inventorization.remaining_pallets.sscc')}
          </TableCell>
          <TableCell align="right">
            {translate('osfc.storage_ui.inventorization.remaining_pallets.ext_id')}
          </TableCell>
          <TableCell>
            {translate('osfc.storage_ui.inventorization.remaining_pallets.product')}
          </TableCell>
          <TableCell align="right">
            {translate('osfc.storage_ui.inventorization.remaining_pallets.units')}
          </TableCell>
          <TableCell align="right">
            {translate('osfc.storage_ui.inventorization.remaining_pallets.weight')}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {/* Index-based key: rows without an sscc would collide otherwise */}
        {rows.map((row, index) => (
          <TableRow key={`${row.order_id}-${row.sscc}-${index}`}>
            <TableCell>
              <OrderReferenceChip orderId={row.order_id} reference={row.order_ref} />
            </TableCell>
            <TableCell>{row.company_name || '—'}</TableCell>
            <TableCell align="right">{row.company_id ?? '—'}</TableCell>
            <TableCell>{row.sscc || '—'}</TableCell>
            <TableCell align="right">{row.ext_id ?? '—'}</TableCell>
            <TableCell>{row.product_name || '—'}</TableCell>
            <TableCell align="right">{row.quantity ?? '—'}</TableCell>
            <TableCell align="right">{row.net_weight ?? '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export const RemainingPalletsDialog: React.FC<RemainingPalletsDialogProps> = ({
  open,
  isLoading,
  isGenerating,
  data,
  onClose,
  onGenerate,
}) => {
  const translate = useTranslate();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const rows = data?.rows ?? [];
  const invalidRows = data?.invalid_rows ?? [];

  const handleConfirm = () => {
    setIsConfirmOpen(false);
    onGenerate();
  };

  // Backdrop click and ESC go through DialogProps.onClose too — ignore them
  // while generating, same as the disabled Close button
  const handleDialogClose = () => {
    if (isGenerating) return;
    onClose();
  };

  return (
    <DialogBase
      open={open}
      title={translate('osfc.storage_ui.inventorization.remaining_pallets.dialog_title')}
      DialogProps={{ maxWidth: 'lg', onClose: handleDialogClose }}
      DialogContentStyles={{ alignItems: 'stretch' }}
      actions={
        <>
          <Button onClick={handleDialogClose} color="secondary" disabled={isGenerating}>
            {translate('ra.action.close')}
          </Button>
          <Button
            onClick={() => setIsConfirmOpen(true)}
            color="primary"
            variant="contained"
            disabled={isLoading || isGenerating || rows.length === 0}
          >
            {translate('osfc.storage_ui.inventorization.remaining_pallets.generate_action')}
          </Button>
          <Confirm
            isOpen={isConfirmOpen}
            title={translate('osfc.storage_ui.inventorization.remaining_pallets.confirm_title')}
            content={translate(
              'osfc.storage_ui.inventorization.remaining_pallets.confirm_content',
              { count: rows.length }
            )}
            onConfirm={handleConfirm}
            onClose={() => setIsConfirmOpen(false)}
          />
        </>
      }
    >
      {isLoading || isGenerating ? (
        <CircularProgress style={{ alignSelf: 'center' }} />
      ) : (
        <>
          {rows.length === 0 ? (
            <Typography variant="body1" align="center">
              {translate('osfc.storage_ui.inventorization.remaining_pallets.empty')}
            </Typography>
          ) : (
            <PalletRowsTable rows={rows} />
          )}
          {invalidRows.length > 0 && (
            <>
              <Typography variant="subtitle1" style={{ marginTop: 16 }}>
                {translate(
                  'osfc.storage_ui.inventorization.remaining_pallets.invalid_rows_title'
                )}
              </Typography>
              <PalletRowsTable rows={invalidRows} />
            </>
          )}
        </>
      )}
    </DialogBase>
  );
};
